import React, { useContext } from "react";
import * as notify from "shared/notify";

import { ShotContext } from "../context/index";
import { PhotographyContext } from "../../../contexts/photography";
import { useSidePanel } from "shared/hooks/use-side-panel";
import { CODING_SHOT } from "../lib/helpers";
import { TaskContext } from "../../../contexts/task";
import { DEFAULT_LOCALE } from "../lib/helpers";
import { validateTask } from "../../../shared/taskState";
import { PhotoQaConfirmation } from "./photo-qa-confirmation";

const PhotoQaNav = ({ onComplete, showEcom, taskKey, isReadOnly }) => {
    const { task } = useContext(TaskContext);
    const { lookups } = useContext(PhotographyContext);
    const { packageTemplate, shotsByLocale, removeShot, selectedLocale } = useContext(ShotContext);
    const { hasMobileBanner } = useSidePanel();
    const isDigitalLite = task.context.shipmentType === 2;

    const validate = async (override = false) => {
        let isValid = true;

        if (packageTemplate.selectedId && lookups.packageTypes.find((x) => x.isActive === false && x.id === packageTemplate.selectedId)) {
            notify.error("Package type is not active anymore. Please select other package Type.");
            isValid = false;

            return false;
        }

        // Must have a package type selected or ECOM shot if NOT sample UPC
        if (
            packageTemplate.selectedId !== "IMMAP" &&
            !task.context.isSampleUpc &&
            !isDigitalLite &&
            ((!packageTemplate.selectedId && !shotsByLocale[DEFAULT_LOCALE.value].find((x) => x.imageViewCode === "Ecom")) ||
                (packageTemplate.selectedId &&
                    !shotsByLocale[DEFAULT_LOCALE.value].find((x) => x.imageViewCode === packageTemplate.defaultImageView)))
        ) {
            notify.error("Must have a package type selected or an Ecom shot");
            isValid = false;

            return false;
        }

        const localesWithoutMinimum = [];
        const localesWithoutImages = [];
        const removeableShots = [];
        const requiredShots = [];
        const filteredShotsByLocale = {};

        Object.keys(shotsByLocale).forEach((key) => {
            shotsByLocale[key]
                .filter((shot) => !shot.uneditedBlobKey || !shot.imageViewCode === CODING_SHOT.imageViewCode)
                .every((shot) => {
                    const isDefaultImageView =
                        showEcom &&
                        shot.localeCode === DEFAULT_LOCALE.value &&
                        packageTemplate.defaultImageView &&
                        packageTemplate.defaultImageView === shot.imageViewCode;

                    const isShotRemovable = !shot.isRequired && !shot.isRequiredWhenAdded && !isDefaultImageView;

                    return isShotRemovable ? removeableShots.push(shot) : requiredShots.push(shot);
                });
        });

        if (removeableShots.length > 0) {
            const confirm = await notify.confirm("This product has empty image shots.  Do you want to delete the empty shots?");

            if (confirm) {
                Object.keys(shotsByLocale).forEach((key) => {
                    selectedLocale.value = key;
                    const filter = shotsByLocale[key].filter((shot) => {
                        return removeableShots.every((remove) => {
                            return shot.imageViewCode === remove.imageViewCode && remove.localeCode === key ? removeShot(shot.imageViewCode) : shot;
                        });
                    });
                    filteredShotsByLocale[key] = filter;
                });
            } else {
                return;
            }
        }

        Object.keys(filteredShotsByLocale).forEach((key) => {
            const shots = filteredShotsByLocale[key];
            // Must have at least 1 shot
            const hasMinShots = shots && shots.length >= 1;

            if (!hasMinShots) {
                localesWithoutMinimum.push(lookups.localeByKey[key].text);
            }

            if (localesWithoutMinimum.length > 0) {
                notify.error(`At least one shot is required for each locale: ${localesWithoutMinimum.join(", ")}`);
                isValid = false;

                return false;
            }

            // All shots have image
            const allShotsHaveImages = shots.every((shot) => !!shot.uneditedBlobKey || shot.imageViewCode === CODING_SHOT.imageViewCode);

            if (allShotsHaveImages === false) {
                localesWithoutImages.push(lookups.localeByKey[key].text);
            }
        });

        if (requiredShots.length > 0) {
            const shotNames = requiredShots.map((shot) => shot.name).join(", ");
            notify.error(`Required shots are empty:<br /> ${shotNames}`);
            isValid = false;
            selectedLocale.value = DEFAULT_LOCALE.value;

            return false;
        }

        if (localesWithoutImages.length > 0) {
            notify.error(`Required shots are empty:<br /> ${localesWithoutImages.join(", ")}`);
            isValid = false;
            selectedLocale.value = DEFAULT_LOCALE.value;

            return false;
        }

        if (isValid && !override) {
            // If Mobile Hero is enabled but not added
            if (hasMobileBanner) {
                const heroShots = shotsByLocale[DEFAULT_LOCALE.value].some((s) => s.imageViewCode === "MHB");

                if (!heroShots) {
                    const confirmHero = await notify.confirm(
                        "This customer is eligible for Verified Mobile Hero image creation. Are you sure you want to complete this task without this image?"
                    );

                    if (!confirmHero) {
                        return (isValid = false);
                    }
                }
            }

            // If Premium, must have 1 shot
            if (task.context.hasPremiumEditing) {
                const premiumShots = shotsByLocale[DEFAULT_LOCALE.value].filter((s) => s.isPremiumEditing);

                if (premiumShots.length <= 0) {
                    const confirmPremium = await notify.confirm(
                        "This product is flagged for premium editing. Are you sure you want to continue without a premium shot?"
                    );

                    if (!confirmPremium) {
                        return (isValid = false);
                    }
                }
            }
        }

        if (isValid) {
            const serverValidate = await validateTask(taskKey, override, { passthrough: true, displayError: true });

            if (serverValidate.errors && serverValidate.errors.length > 0) {
                isValid = false;

                return false;
            }

            if (!override && serverValidate.overridableErrors && serverValidate.overridableErrors.length > 0) {
                const displayErrors = serverValidate.overridableErrors.join(", ");
                const confirmOverride = await notify.confirm(`${displayErrors}`);

                if (confirmOverride) {
                    return validate(true);
                } else {
                    return false;
                }
            }
        }

        return isValid;
    };

    return <PhotoQaConfirmation onComplete={onComplete} validate={validate} taskId={taskKey} isReadOnly={isReadOnly} />;
};

export default PhotoQaNav;
